import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { deleteUser } from "firebase/auth";
import { Header } from "../../components/Header/Header";
import { Footer } from "../../components/Footer/Footer";
import { useAuth } from "../../../context/AuthContext";
import { auth } from "../../../service/firebase";
import {
  deleteUserAccountData,
  getUserProfile
} from "../../../service/userService";
import { listTurmasForUser } from "../../../service/turmaService";
import { countActiveFriends } from "../../../service/friendService";
import "./ProfileSettings.css";

const ProfileSettings = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [profileData, setProfileData] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState("");
  const [userTurmas, setUserTurmas] = useState([]);
  const [turmasLoading, setTurmasLoading] = useState(true);
  const [turmasError, setTurmasError] = useState("");
  const [friendsCount, setFriendsCount] = useState(0);
  const [friendsLoading, setFriendsLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setProfileData(null);
      setProfileLoading(false);
      setUserTurmas([]);
      setTurmasLoading(false);
      setFriendsCount(0);
      setFriendsLoading(false);
      return;
    }

    const loadProfile = async () => {
      setProfileLoading(true);
      setProfileError("");
      try {
        const data = await getUserProfile(user.uid);
        setProfileData(data);
      } catch (error) {
        console.error("Erro ao carregar perfil", error);
        setProfileError("Não foi possível carregar seus dados agora.");
      } finally {
        setProfileLoading(false);
      }
    };

    const loadTurmas = async () => {
      setTurmasLoading(true);
      setTurmasError("");
      try {
        const turmas = await listTurmasForUser(user.uid);
        setUserTurmas(turmas);
      } catch (error) {
        console.error("Erro ao carregar turmas do usuário", error);
        setTurmasError("Não foi possível carregar suas turmas agora.");
      } finally {
        setTurmasLoading(false);
      }
    };

    const loadFriends = async () => {
      setFriendsLoading(true);
      try {
        const total = await countActiveFriends(user.uid);
        setFriendsCount(total);
      } catch (error) {
        console.error("Erro ao contar amigos", error);
        setFriendsCount(0);
      } finally {
        setFriendsLoading(false);
      }
    };

    loadProfile();
    loadTurmas();
    loadFriends();
  }, [user]);

  const fallbackName = user?.displayName || user?.email || "Jogador";
  const nickname = profileData?.nickname;
  const primaryName = nickname || fallbackName;
  const secondaryName = nickname ? fallbackName : "";

  const formatBirthDate = (value) => {
    if (!value) return null;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }
    return parsed.toLocaleDateString("pt-BR");
  };

  const birthLabel = formatBirthDate(profileData?.birth);
  const alturaLabel = profileData?.altura ? `${profileData.altura} cm` : "Não informado";
  const pesoLabel = profileData?.peso ? `${profileData.peso} kg` : "Não informado";

  const handleNavigateFriends = () => {
    navigate("/amigos");
  };

  const handleNavigateTurma = (turmaId) => {
    navigate(`/turmas/${turmaId}`);
  };

  const handleDeleteAccount = async () => {
    if (!user) return;
    const confirmed = window.confirm(
      "Tem certeza de que deseja excluir sua conta Palm3? Essa ação não poderá ser desfeita."
    );
    if (!confirmed) {
      return;
    }

    setDeleteError("");
    setIsDeleting(true);

    let restoreAccountData = null;

    try {
      restoreAccountData = await deleteUserAccountData(user.uid);
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("Usuario autenticado não encontrado.");
      }
      await deleteUser(currentUser);
      navigate("/");
    } catch (error) {
      console.error("Erro ao excluir conta", error);
      if (restoreAccountData) {
        try {
          await restoreAccountData();
        } catch (restoreError) {
          console.error("Erro ao restaurar dados após falha na exclusão", restoreError);
        }
      }

      if (error.code === "auth/requires-recent-login") {
        setDeleteError("Por segurança, faça login novamente e tente excluir sua conta.");
      } else {
        setDeleteError("Não foi possível excluir sua conta agora. Tente novamente em instantes.");
      }
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Header />
      <div className="body">
        <div className="center">
          <div className="body-content card profile-card">
            <div className="topo-page">
              <strong># Perfil</strong>
            </div>
            {user ? (
              <>
                <div className="profile-overview">
                  <div className="profile-avatar profile-avatar-small">
                    {user.photoURL ? (
                      <img src={user.photoURL} alt="Foto de perfil" />
                    ) : (
                      <span className="profile-avatar-placeholder">
                        {(primaryName || "?").charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="profile-main-info">
                    <div className="profile-main-info-top">
                      <h2>{primaryName}</h2>
                      {secondaryName && (
                        <p className="profile-secondary-name">{secondaryName}</p>
                      )}
                      <p className="profile-email">{user.email}</p>
                    </div>
                    <div className="profile-meta">
                      <div>
                        <span className="label">Apelido</span>
                        <strong>{profileData?.nickname || fallbackName}</strong>
                      </div>
                      <div>
                        <span className="label">Nascimento</span>
                        <strong>{birthLabel || "Não informado"}</strong>
                      </div>
                      <div>
                        <span className="label">Altura</span>
                        <strong>{alturaLabel}</strong>
                      </div>
                      <div>
                        <span className="label">Peso</span>
                        <strong>{pesoLabel}</strong>
                      </div>
                      {user.metadata?.creationTime && (
                        <div>
                          <span className="label">Palm3 desde</span>
                          <strong>
                            {new Date(user.metadata.creationTime).toLocaleDateString("pt-BR")}
                          </strong>
                        </div>
                      )}
                    </div>
                    <Link to="/perfil/editar" className="profile-edit-link">
                      Editar perfil
                    </Link>
                    {profileError && (
                      <span className="profile-error-text">{profileError}</span>
                    )}
                  </div>
                </div>
                <div className="profile-stats">
                  <div className="profile-stats-header">
                    <h4>Seus números</h4>
                    {profileLoading && <span>Carregando...</span>}
                  </div>
                  <div className="profile-stats-grid">
                    {[
                      { label: "Posição", value: profileData?.posicao || "-", emoji: "📍" },
                      { label: "Overall", value: profileData?.overall ?? 65, emoji: "🔥" },
                      { label: "Partidas", value: profileData?.qt_partidas ?? 0, emoji: "📊" },
                      { label: "Gols", value: profileData?.gols ?? 0, emoji: "⚽" },
                      { label: "Assistências", value: profileData?.assistencias ?? 0, emoji: "🎯" },
                      { label: "Vitórias", value: profileData?.vitorias ?? 0, emoji: "🏆" },
                      { label: "Derrotas", value: profileData?.derrotas ?? 0, emoji: "⚔️" },
                      { label: "Empates", value: profileData?.empates ?? 0, emoji: "🤝" },
                      { label: "Sem gol", value: profileData?.jogosSemTomarGol ?? 0, emoji: "🛡️" },
                      {
                        label: "Influência",
                        value: Number(profileData?.influenciaVitoria ?? 0).toFixed(2),
                        emoji: "📈"
                      }
                    ].map((stat) => (
                      <div key={stat.label} className="profile-stat-card">
                        <span>
                          <span className="profile-stat-emoji" aria-hidden="true">
                            {stat.emoji}
                          </span>
                          {stat.label}
                        </span>
                        <strong>{stat.value}</strong>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="profile-quick-actions">
                  <button
                    type="button"
                    className="profile-friends-card"
                    onClick={handleNavigateFriends}
                  >
                    <div>
                      <span>Amigos</span>
                      <strong>
                        {friendsLoading ? "..." : `${friendsCount}`}
                      </strong>
                    </div>
                    <span className="profile-friends-link">Ver lista</span>
                  </button>
                </div>
                <div className="profile-turmas-section">
                  <div className="profile-section-header">
                    <h4>Suas turmas</h4>
                    {turmasLoading && <span>Carregando...</span>}
                  </div>
                  {turmasError ? (
                    <p className="profile-error-text" role="alert">
                      {turmasError}
                    </p>
                  ) : userTurmas.length === 0 ? (
                    <p className="profile-empty-turmas">
                      Você ainda não participa de nenhuma turma.
                    </p>
                  ) : (
                    <div className="profile-turmas-list">
                      {userTurmas.map((turma) => (
                        <button
                          key={turma.id}
                          type="button"
                          className="profile-turma-card"
                          onClick={() => handleNavigateTurma(turma.id)}
                        >
                          <div>
                            <strong>{turma.nome}</strong>
                            <span>
                              {turma.local || turma.descricao || "Ver detalhes"}
                            </span>
                          </div>
                          <span className="profile-turma-link">Abrir</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="profile-danger-zone">
                  <h4>Excluir conta</h4>
                  <p>
                    Ao confirmar, você será desconectado imediatamente e todos os dados ligados ao seu perfil
                    serão removidos. Essa ação é permanente.
                  </p>
                  {deleteError && (
                    <p className="profile-danger-error" role="alert">
                      {deleteError}
                    </p>
                  )}
                  <button
                    className="profile-delete-button"
                    type="button"
                    onClick={handleDeleteAccount}
                    disabled={isDeleting}
                  >
                    {isDeleting ? "Excluindo conta..." : "Deletar minha conta"}
                  </button>
                </div>
              </>
            ) : (
              <div className="profile-empty">
                <p>Você precisa estar logado para acessar o seu perfil.</p>
                <Link className="btn-default" to="/login">
                  Ir para login
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ProfileSettings;
